/**
 * 客诉申诉凭证文件的前端校验规则（按文件类型限制大小，视频额外限时长）。
 *
 * - 图片（image/*）：≤ 20MB
 * - 视频（video/*）：≤ 500MB 且时长 ≤ 15s（浏览器读不出时长则只校大小，放行时长）
 * - 其它文件：≤ 100MB
 *
 * 校验失败返回 errorKey（i18n key）+ params，由调用方 t() 翻译后提示。
 */

const MB = 1024 * 1024;

export const APPEAL_FILE_MAX_IMAGE_MB = 20;
export const APPEAL_FILE_MAX_VIDEO_MB = 500;
export const APPEAL_FILE_MAX_OTHER_MB = 100;
export const APPEAL_VIDEO_MAX_DURATION_SECONDS = 15;

export interface AppealFileValidateResult {
    ok: boolean;
    /** 校验失败时的 i18n key */
    errorKey?: string;
    /** i18n 插值参数 */
    params?: Record<string, number>;
}

function isImage(file: File): boolean {
    return file.type.startsWith('image/');
}

function isVideo(file: File): boolean {
    return file.type.startsWith('video/');
}

/** 读取视频时长（秒）；浏览器无法解码（如部分 HEVC/.mov）时返回 null */
function readVideoDuration(file: File): Promise<number | null> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        const cleanup = () => URL.revokeObjectURL(url);
        video.onloadedmetadata = () => {
            cleanup();
            const d = video.duration;
            resolve(Number.isFinite(d) && d > 0 ? d : null);
        };
        video.onerror = () => {
            cleanup();
            resolve(null);
        };
        video.src = url;
    });
}

/**
 * 校验单个申诉凭证文件。视频需读元数据，故为异步。
 */
export async function validateAppealFile(file: File): Promise<AppealFileValidateResult> {
    if (isImage(file)) {
        if (file.size > APPEAL_FILE_MAX_IMAGE_MB * MB) {
            return {ok: false, errorKey: 'customerComplaint.drawer.imageTooLarge', params: {max: APPEAL_FILE_MAX_IMAGE_MB}};
        }
        return {ok: true};
    }
    if (isVideo(file)) {
        if (file.size > APPEAL_FILE_MAX_VIDEO_MB * MB) {
            return {ok: false, errorKey: 'customerComplaint.drawer.videoTooLarge', params: {max: APPEAL_FILE_MAX_VIDEO_MB}};
        }
        const duration = await readVideoDuration(file);
        // 读不出时长（如 HEVC 浏览器不解码）则放行，仅以大小为准
        if (duration !== null && duration > APPEAL_VIDEO_MAX_DURATION_SECONDS) {
            return {
                ok: false,
                errorKey: 'customerComplaint.drawer.videoTooLong',
                params: {max: APPEAL_VIDEO_MAX_DURATION_SECONDS},
            };
        }
        return {ok: true};
    }
    if (file.size > APPEAL_FILE_MAX_OTHER_MB * MB) {
        return {ok: false, errorKey: 'customerComplaint.drawer.fileTooLarge', params: {max: APPEAL_FILE_MAX_OTHER_MB}};
    }
    return {ok: true};
}
